import React, { useState, useEffect, useCallback } from "react";
import { Clock, Trash2, Film } from "@/icons/lucide-compat";
import { ToolcraftIconButton as IconButton } from "@openreel/ui";
import { ToolcraftText as Text } from "@openreel/ui";
import {
  checkForRecovery,
  recoverProject,
  type AutoSaveMetadata,
} from "../../services/auto-save";
import { loadMediaBlob } from "../../services/media-storage";
import { getMediaBridge, initializeMediaBridge } from "../../bridges/media-bridge";
import { useProjectStore } from "../../stores/project-store";
import { CARD_BASE, FOCUS_RING } from "../surface";
import { useAnalytics, AnalyticsEvents } from "../../hooks/useAnalytics";

interface RecentProject {
  id: string;
  saveId: string;
  name: string;
  lastModified: number;
  /** Opening frame of the project, when it has footage in it. */
  thumbnail?: string;
}

/**
 * Draw a poster for cards whose saved project never recorded one.
 *
 * Projects saved before posters were stored as data: URLs carry no thumbnail
 * at all, and they only heal when the project is next opened. That left a wall
 * of identical film glyphs for anyone who had not reopened everything, which
 * is precisely the state this card was meant to fix. So render one now from
 * the media already in the browser.
 *
 * Sequential and best effort on purpose: each of these decodes a video frame,
 * and a dashboard is not worth stalling for. Cards fill in as they resolve,
 * and the result is deliberately NOT written back here, because the next
 * ordinary save of that project stores it properly through the normal path.
 */
async function backfillMissingPosters(
  projects: RecentProject[],
  apply: React.Dispatch<React.SetStateAction<RecentProject[]>>,
): Promise<void> {
  const missing = projects.filter((p) => !p.thumbnail);
  if (missing.length === 0) return;

  try {
    if (!getMediaBridge().isInitialized()) await initializeMediaBridge();
  } catch {
    return;
  }

  for (const entry of missing) {
    try {
      const project = await recoverProject(entry.saveId);
      const clip = project?.timeline.tracks
        .flatMap((track) => track.clips)
        .filter((c) => !/^(text|shape|svg|sticker)-/.test(c.mediaId))
        .sort((a, b) => a.startTime - b.startTime)[0];
      if (!clip) continue;

      const item = project?.mediaLibrary.items.find(
        (m) => m.id === clip.mediaId,
      );
      const blob = item?.blob ?? (await loadMediaBlob(clip.mediaId));
      if (!blob) continue;

      const thumbs = await getMediaBridge().generateThumbnailsForMedia(
        blob,
        item?.type === "image" ? "image" : "video",
      );
      const poster = thumbs[0]?.dataUrl;
      if (!poster) continue;

      apply((prev) =>
        prev.map((p) => (p.id === entry.id ? { ...p, thumbnail: poster } : p)),
      );
    } catch {
      // One unreadable project must not stop the rest from filling in.
    }
  }
}

interface RecentProjectsProps {
  onProjectSelected?: () => void;
  /** How many cards to show. The dashboard strip shows a slice, not all ten. */
  limit?: number;
  /**
   * Drop the built-in heading and footnote. The dashboard draws its own strip
   * header and states the local-storage caveat once at the bottom of the page,
   * rather than under every list.
   */
  hideChrome?: boolean;
}

export const RecentProjects: React.FC<RecentProjectsProps> = ({
  onProjectSelected,
  limit = 10,
  hideChrome = false,
}) => {
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
  const recoverFromAutoSave = useProjectStore(
    (state) => state.recoverFromAutoSave,
  );
  const { track } = useAnalytics();

  useEffect(() => {
    async function loadProjects() {
      try {
        const saves = await checkForRecovery();
        const projectMap = new Map<string, AutoSaveMetadata>();

        for (const save of saves) {
          if (!projectMap.has(save.projectId)) {
            projectMap.set(save.projectId, save);
          }
        }

        const projects: RecentProject[] = Array.from(projectMap.values())
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, limit)
          .map((save) => ({
            id: save.projectId,
            saveId: save.id,
            name: save.projectName,
            lastModified: save.timestamp,
            thumbnail: save.thumbnail,
          }));

        setRecentProjects(projects);
        void backfillMissingPosters(projects, setRecentProjects);
      } catch (error) {
        console.error("Failed to load recent projects:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadProjects();
  }, [limit]);

  const handleSelectProject = useCallback(
    async (project: RecentProject) => {
      setLoadingProjectId(project.id);
      try {
        const success = await recoverFromAutoSave(project.saveId);
        if (success) {
          track(AnalyticsEvents.PROJECT_OPENED, {
            source: "recent_projects",
          });
          onProjectSelected?.();
        }
      } catch (error) {
        console.error("Failed to load project:", error);
      } finally {
        setLoadingProjectId(null);
      }
    },
    [recoverFromAutoSave, onProjectSelected, track],
  );

  const handleRemoveProject = useCallback(
    (projectId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      setRecentProjects((prev) => prev.filter((p) => p.id !== projectId));
    },
    [],
  );

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-white/15 border-t-white/60" />
        <Text type="supporting" color="secondary" className="text-[14px] text-[#8f8f8f]">
          Loading recent projects...
        </Text>
      </div>
    );
  }

  if (recentProjects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-[14px] bg-white/[0.05]">
          <Clock size={24} className="text-[#8f8f8f]" />
        </div>
        <Text type="body" color="primary" weight="medium" className="mb-2 text-[16px] text-white">
          No recent projects
        </Text>
        <Text type="supporting" color="secondary" className="max-w-md text-center text-[14px] text-[#8f8f8f]">
          Your recently opened projects will appear here. Start a new project or
          use a template to get started.
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hideChrome ? null : (
        <div className="flex items-center justify-between">
          <Text
            type="label"
            color="primary"
            weight="medium"
            className="text-[15px] text-white"
          >
            Recent projects ({recentProjects.length})
          </Text>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {recentProjects.map((project) => {
          const isLoadingThis = loadingProjectId === project.id;
          return (
            <div
              key={project.id}
              className={`${CARD_BASE} ${isLoadingThis ? "opacity-70" : ""}`}
            >
              {/* A plain button rather than ClickableCard: that control draws
                  its own 7px corner and hairline, which would show as an inner
                  box inside this card's 14px borderless frame. */}
              <button
                type="button"
                aria-label={`Open ${project.name}`}
                disabled={isLoadingThis}
                onClick={() => handleSelectProject(project)}
                className={`flex flex-1 flex-col text-left ${FOCUS_RING} disabled:cursor-not-allowed`}
              >
                {/* The project's own opening frame. Every card used to be the
                    same film glyph, which told you nothing about which project
                    you were looking at. */}
                <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-[#08080a]">
                  {isLoadingThis ? (
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/15 border-t-white/60" />
                  ) : project.thumbnail ? (
                    <img
                      src={project.thumbnail}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Film size={32} className="text-white/20" />
                  )}
                </div>

                <div className="flex-1 px-3.5 pb-3.5 pt-3">
                  <Text type="supporting" color="primary" weight="medium" className="truncate text-[14px] font-medium text-white">
                    {project.name}
                  </Text>
                  <div className="mt-1 flex items-center gap-1.5 text-[13px] text-[#8f8f8f]">
                    <Clock size={12} />
                    <span>{formatDate(project.lastModified)}</span>
                  </div>
                </div>
              </button>

              <IconButton
                label="Remove from recent"
                onClick={(e) => handleRemoveProject(project.id, e)}
                icon={<Trash2 size={14} aria-hidden />}
                size="sm"
                variant="ghost"
                className="absolute right-2 top-2 rounded-lg bg-black/60 p-1.5 text-white/70 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/80 hover:text-red-400 group-hover:opacity-100"
              />
            </div>
          );
        })}
      </div>

      {hideChrome ? null : (
        <Text
          type="supporting"
          color="secondary"
          className="text-center text-[13px] text-[#8f8f8f]"
        >
          Recent projects are stored locally in your browser
        </Text>
      )}
    </div>
  );
};

export default RecentProjects;
